import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mantine/core';
import { ArrowLeft } from 'tabler-icons-react';
import ResponsiveWebstoreModal from './webstore/ResponsiveWebstoreModal';
import { useMediaQueries } from '../../../helpers/hooks';
import WebstoreProductDetailsPage from './WebstoreProductDetailsPage';
import { Context as HelperContext } from '../../../providers/HelperContextProvider';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import WebstoreProductsGridPage from './WebstoreProductsGridPage';
import RegistrationCartSummary from '../associations/RegistrationCartSummary';
import { useRegCartController } from '../../../helpers/shop/useRegCartController';
import ClearCartConfirmation from '../checkout/ClearCartConfirmation';
import { triggerNotification } from '../../../helpers/notificationHelper';
import PreviewWebstoreProductModal from './webstore/PreviewWebstoreProductModal';

const themeConfig = {};

const colorSchemes = [
  {
    textColor: '#000000',
    backgroundColor: '#ffffff'
  }
];

const WebstoreProductsModal = ({ regAssociation, isCheckoutView }) => {
  const mediaQueries = useMediaQueries();
  const { isTabletOrSmaller } = mediaQueries;
  const {
    state: helperState,
    toggleWebstoreProductsShowcaseModal
  } = useContext(HelperContext);
  const { userCart, addCartProducts } = useRegCartController();
  const [isResubmitting, setIsResubmitting] = useState(false);

  useEffect(
    () => () => {
      // eslint-disable-next-line no-use-before-define
      onClose();
    },
    []
  );

  const onClose = () => {
    toggleWebstoreProductsShowcaseModal({
      ...helperState.webstore.productsShowcaseModal,
      isOpen: false
    });
  };

  const onClearCartToAddProduct = () => {
    setIsResubmitting(true);
    addCartProducts(
      {
        clearCart: true,
        ...helperState.webstore.productsShowcaseModal.resubmitState
      },
      () => {
        triggerNotification('Product added to cart', 'Success', 'green');
        setIsResubmitting(false);
        toggleWebstoreProductsShowcaseModal({
          ...helperState.webstore.productsShowcaseModal,
          showCheckoutSummary: !isCheckoutView,
          showClearCart: false,
          resubmitState: null
        });
      },
      (message) => {
        triggerNotification(message);
        setIsResubmitting(false);
      }
    );
  };

  return (
    <ResponsiveWebstoreModal
      alignModal={isTabletOrSmaller ? 'end' : 'center'}
      isOpen={helperState.webstore.productsShowcaseModal.isOpen}
      noHeader
      onClose={onClose}
      radius={0}
      size={
        helperState.webstore.productsShowcaseModal.showClearCart
          ? 600
          : helperState.webstore.productsShowcaseModal.showCheckoutSummary
          ? 800
          : 1200
      }
    >
      {helperState.webstore.productsShowcaseModal.showClearCart ? (
        <ClearCartConfirmation
          loading={isResubmitting}
          onClose={onClose}
          onContinue={onClearCartToAddProduct}
          regAssociation={regAssociation}
        />
      ) : helperState.webstore.productsShowcaseModal.showCheckoutSummary ? (
        <RegistrationCartSummary
          ecomStoreProduct={
            helperState.webstore.productsShowcaseModal.ecomStoreProduct
          }
          ecomStoreProductVariant={
            helperState.webstore.productsShowcaseModal.ecomStoreProductVariant
          }
          onClose={onClose}
          regAssociation={regAssociation}
          regCart={userCart}
          showImageNotAvailablePreview={
            helperState.webstore.productsShowcaseModal
              .enableImageNotAvailablePreview
          }
        />
      ) : helperState.webstore.productsShowcaseModal.ecomStoreProduct ||
        helperState.webstore.productsShowcaseModal.productUuid ||
        helperState.webstore.productsShowcaseModal.urlHandle ? (
        <AppStack>
          <AppFlexbox>
            <Button
              color="dark"
              leftSection={<ArrowLeft color="#000" size={20} />}
              onClick={() => {
                toggleWebstoreProductsShowcaseModal({
                  ...helperState.webstore.productsShowcaseModal,
                  ecomStoreProduct: null,
                  urlHandle: null,
                  productUuid: null,
                  vendorVariantUuid: null
                });
              }}
              size="compact-md"
              variant="subtle"
            >
              View all products
            </Button>
          </AppFlexbox>

          <WebstoreProductDetailsPage
            analyticCta={helperState.webstore.productsShowcaseModal.analyticCta}
            colorSchemes={colorSchemes}
            defaultVendorVariantUuid={
              helperState.webstore.productsShowcaseModal.vendorVariantUuid
            }
            ecomStore={regAssociation?.ecomStore}
            isCheckoutView={isCheckoutView}
            mediaQueries={mediaQueries}
            regAssociation={regAssociation}
            themeConfig={themeConfig}
            urlHandle={
              helperState.webstore.productsShowcaseModal.ecomStoreProduct
                ?.urlHandle ||
              helperState.webstore.productsShowcaseModal.urlHandle
            }
            uuid={helperState.webstore.productsShowcaseModal.productUuid}
          />
        </AppStack>
      ) : (
        <WebstoreProductsGridPage
          analyticCta={helperState.webstore.productsShowcaseModal.analyticCta}
          colorSchemes={colorSchemes}
          ecomStore={regAssociation?.ecomStore}
          mediaQueries={mediaQueries}
          regAssociation={regAssociation}
          themeConfig={themeConfig}
        />
      )}

      <PreviewWebstoreProductModal />
    </ResponsiveWebstoreModal>
  );
};

WebstoreProductsModal.propTypes = {
  isCheckoutView: PropTypes.bool,
  regAssociation: PropTypes.object
};

export default WebstoreProductsModal;
